import SEOHead from '@/components/base/SEOHead';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { featuredArticles } from '@/mocks/articles';
import { articleContent } from '@/mocks/articleContent';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://superbuyspread.pics';

const relatedArticlesMap: Record<string, typeof featuredArticles> = {
  'superbuy-sheet-complete-guide': featuredArticles.filter((a) => a.slug !== 'superbuy-sheet-complete-guide').slice(0, 3),
  'superbuy-sheet-vs-manual-browsing': featuredArticles.filter((a) => a.slug !== 'superbuy-sheet-vs-manual-browsing').slice(0, 3),
  'superbuy-sheet-beginner-tutorial': featuredArticles.filter((a) => a.slug !== 'superbuy-sheet-beginner-tutorial').slice(0, 3),
  'superbuy-sheet-safety-tips': featuredArticles.filter((a) => a.slug !== 'superbuy-sheet-safety-tips').slice(0, 3),
  'superbuy-sheet-top-categories': featuredArticles.filter((a) => a.slug !== 'superbuy-sheet-top-categories').slice(0, 3),
  'superbuy-sheet-best-finds': featuredArticles.filter((a) => a.slug !== 'superbuy-sheet-best-finds').slice(0, 3),
  'superbuy-sheet-shopping-guide': featuredArticles.filter((a) => a.slug !== 'superbuy-sheet-shopping-guide').slice(0, 3),
  'superbuy-sheet-size-guide': featuredArticles.filter((a) => a.slug !== 'superbuy-sheet-size-guide').slice(0, 3),
  'superbuy-sheet-trending-2026': featuredArticles.filter((a) => a.slug !== 'superbuy-sheet-trending-2026').slice(0, 3),
};

function buildArticleJsonLd(article: NonNullable<(typeof articleContent)[string]>, slug: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.metaDescription,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Organization',
      name: 'Superbuy Sheet Research Team',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Superbuy Sheet',
      url: siteUrl,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/article/${slug}`,
    },
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleContent[slug] : null;
  const related = slug ? (relatedArticlesMap[slug] || featuredArticles.slice(0, 3)) : featuredArticles.slice(0, 3);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center py-20">
            <h1 className="font-heading text-3xl font-bold text-foreground-900">Article Not Found</h1>
            <p className="mt-4 text-foreground-500">The article you are looking for does not exist.</p>
            <Link to="/" className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              Back to Home
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleJsonLd = buildArticleJsonLd(article, slug || '');

  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead jsonLd={articleJsonLd} />
      <Navbar />

      <section className="relative pt-20 pb-12 md:pt-28 md:pb-20 bg-background-100">
        <div className="absolute inset-0 overflow-hidden opacity-15">
          <img
            src="https://readdy.ai/api/search-image?query=Overhead%20editorial%20photography%20of%20modern%20workspace%20with%20fashionable%20sneakers%20hoodies%20and%20shopping%20lists%20spread%20on%20oak%20wood%20desk%20warm%20golden%20hour%20lighting%20cosy%20creative%20studio%20atmosphere%20rich%20textures%20product%20curation%20aesthetic&width=1600&height=900&seq=article-hero-bg-2026-v2&orientation=landscape"
            alt="Superbuy Sheet article background"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 text-xs font-semibold text-accent-700 bg-accent-100 rounded-full mb-4">
              {article.category}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 tracking-tight leading-tight">
              {article.title}
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                Superbuy Sheet Research Team
              </span>
              <span className="text-foreground-300">|</span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="text-foreground-300">|</span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
            </div>
          </div>
        </div>
      </section>

      <article className="py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
            <div className="lg:col-span-3">
              <div className="prose-custom max-w-none">
                <p className="text-lg text-foreground-600 leading-relaxed mb-10">
                  {article.intro}
                </p>

                {article.sections.map((section, idx) => (
                  <div key={idx} className="mb-10">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                      {section.heading}
                    </h2>
                    <div className="text-foreground-600 leading-relaxed text-base space-y-4">
                      {section.content.split('\n\n').map((para, pIdx) => (
                        <p key={pIdx}>{para}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 p-6 bg-background-100 rounded-xl border border-background-200/70">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">About the Author</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  This article was researched and written by the <strong className="text-foreground-700">Superbuy Sheet Research Team</strong>. All guides are researched, reviewed and updated regularly to provide accurate and useful Superbuy Sheet information. Our editorial standards ensure that every piece of content meets our criteria for accuracy, clarity, and practical value for readers.
                </p>
                <p className="mt-2 text-xs text-foreground-400">
                  Last updated: {article.date}
                </p>
              </div>

              <div className="mt-8 text-center">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to Homepage
                </Link>
              </div>
            </div>

            <aside className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                <div className="bg-primary-600 rounded-xl p-6 text-white">
                  <h3 className="font-heading text-lg font-semibold">Ready to Shop?</h3>
                  <p className="mt-2 text-sm text-white/80">
                    Jump to the main Superbuy Sheet platform and start discovering products now.
                  </p>
                  <div className="mt-4 space-y-2">
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="block w-full text-center px-4 py-2.5 bg-accent-500 text-background-50 text-sm font-semibold rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      superbuy sheet
                    </a>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="block w-full text-center px-4 py-2.5 bg-white/15 text-white text-sm font-semibold rounded-full hover:bg-white/25 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      buy it now
                    </a>
                  </div>
                </div>

                <div className="bg-secondary-100 rounded-xl p-6">
                  <h4 className="text-xs font-semibold text-secondary-700 uppercase tracking-wider mb-3">Quick Facts</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line text-accent-600 mt-0.5"></i>
                      14 product categories
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line text-accent-600 mt-0.5"></i>
                      Updated weekly
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line text-accent-600 mt-0.5"></i>
                      100% free to browse
                    </li>
                    <li className="flex items-start gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line text-accent-600 mt-0.5"></i>
                      No account required
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">Related Articles</h4>
                  <div className="space-y-4">
                    {related.map((rel) => (
                      <Link
                        key={rel.slug}
                        to={`/article/${rel.slug}`}
                        className="block group cursor-pointer"
                      >
                        <h5 className="text-sm font-semibold text-foreground-800 group-hover:text-accent-600 transition-colors leading-snug">
                          {rel.title}
                        </h5>
                        <span className="text-xs text-foreground-400 mt-1 inline-block">
                          {rel.readTime}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>

                <Link
                  to="/"
                  className="flex items-center gap-2 text-sm text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer"
                >
                  <i className="ri-home-line"></i>
                  Back to Homepage
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}